import { useState, useEffect } from 'react';
import api from '../../api/client';
import StreamStatus from './StreamStatus';
import { exportWorkbook } from '../../utils/export-xlsx';

// Panel compacto de oyentes en vivo.
// Layout: una línea de números + chips de servidores + tabla compacta con
// scroll y filas expandibles. El StreamStatus completo queda plegado.
//
// Datos: snapshot `geo` (listener-geo, embebido en /api/dashboard).

const KIND_META = {
  app: { label: 'App', icon: '📱', chip: 'bg-primary-900/30 text-primary-300 border-primary-700/50' },
  web: { label: 'Web', icon: '🌐', chip: 'bg-blue-900/30 text-blue-300 border-blue-700/50' },
  player: { label: 'Reproductor', icon: '▶️', chip: 'bg-amber-900/30 text-amber-300 border-amber-700/50' },
  monitor: { label: 'Monitor', icon: '🖥️', chip: 'bg-dark-800 text-dark-300 border-dark-700' },
  bot: { label: 'Bot', icon: '🤖', chip: 'bg-dark-800 text-dark-400 border-dark-700' },
  unknown: { label: 'Desconocido', icon: '❔', chip: 'bg-dark-800 text-dark-300 border-dark-700' },
};

const SUMMARY = [
  { k: 'total', icon: '👥', label: 'Total', hint: 'Oyentes reales: externos + locales, sin relay ni monitor' },
  { k: 'located', icon: '🌍', label: 'Con ubicación', hint: 'Oyentes a los que se les conoce la ciudad/país en este momento' },
  { k: 'app', icon: '📱', label: 'App Expo', hint: 'Confirmados por la app (heartbeat src=app)' },
  { k: 'web', icon: '🌐', label: 'Web', hint: 'Player de culturedj.com/radio' },
  { k: 'players', icon: '▶️', label: 'Reproductores', hint: 'VLC, TV, IPTV… detectados por User-Agent' },
  { k: 'unattributed', icon: '❔', label: 'Sin identificar', hint: 'Conexiones sin heartbeat: el proveedor no expone IP/UA' },
  { k: 'monitor', icon: '🖥️', label: 'Monitor', hint: 'Player de este panel' },
];

const flagOf = (cc) =>
  cc && /^[A-Za-z]{2}$/.test(cc)
    ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : '🌐';

const fmtDur = (s) => {
  if (s == null || Number.isNaN(s)) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const fmtFull = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtClock = (ts) => {
  if (!ts) return '—';
  const d = new Date(Number(ts) || ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtAgo = (ts) => {
  if (!ts) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(Number(ts) || ts).getTime()) / 1000));
  if (s < 90) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 90) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
};

// Barra de proporción horizontal (países / ciudades).
function GeoBar({ label, flag, count, max, tone = 'bg-primary-500' }) {
  const w = max > 0 ? Math.max(6, Math.round((count / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-1.5" title={`${label} — ${count} oyente(s)`}>
      <span className="w-4 shrink-0 text-center text-[11px]">{flag}</span>
      <span className="min-w-0 flex-1 truncate text-[11px] text-dark-200">{label}</span>
      <span className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-dark-700">
        <span className={`block h-full rounded-full ${tone}`} style={{ width: `${w}%` }} />
      </span>
      <b className="w-4 shrink-0 text-right text-[11px] tabular-nums text-dark-100">{count}</b>
    </div>
  );
}

// Bloque "Dónde escuchan ahora": distribución geográfica en vivo.
function GeoBreakdown({ geo }) {
  const countries = geo.countries || [];
  const cities = geo.cities || [];
  const total = geo.total || 0;
  const located = geo.located || 0;
  const attr = geo.attribution || {};
  const unidentified = attr.unidentified != null ? attr.unidentified : geo.unattributed || 0;
  const maxCountry = countries.length ? countries[0].count : 0;
  const maxCity = cities.length ? cities[0].count : 0;
  const blind = attr.blind_servers || [];
  const denom = Math.max(total, located);

  return (
    <div className="mb-2 rounded-lg border border-dark-700 bg-dark-900/40 p-2">
      {located === 0 ? (
        <p className="mt-1.5 text-[10px] text-dark-400">
          Todavía no se pudo ubicar a nadie. Los oyentes aparecen ~10 s después de conectarse
          {unidentified > 0 && ' (los que no mandan señal no se pueden ubicar)'}.
        </p>
      ) : (
        <div className="grid max-h-24 grid-cols-1 gap-x-4 gap-y-1 overflow-y-auto pr-1 sm:grid-cols-2">
          <div>
            <p className="mb-0.5 text-[9px] uppercase tracking-wide text-dark-500">Países</p>
            <div className="space-y-0.5">
              {countries.slice(0, 6).map((c) => (
                <GeoBar
                  key={c.country_code || c.country}
                  label={c.country || '—'}
                  flag={flagOf(c.country_code)}
                  count={c.count}
                  max={maxCountry}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-0.5 text-[9px] uppercase tracking-wide text-dark-500">Ciudades</p>
            <div className="space-y-0.5">
              {cities.slice(0, 6).map((c) => (
                <GeoBar
                  key={(c.city || '?') + (c.country_code || '')}
                  label={[c.city, c.country].filter(Boolean).join(', ') || '—'}
                  flag={flagOf(c.country_code)}
                  count={c.count}
                  max={maxCity}
                  tone="bg-success-500"
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {unidentified > 0 && (
        <p className="mt-1.5 text-[10px] leading-snug text-warning-400">
          ⚠ {unidentified} oyente(s) sin ubicación: {blind.length > 0
            ? `${blind.map((s) => `"${s.name}"`).join(', ')} no expone la IP de sus conexiones`
            : 'no mandó señal ni se pudo atribuir'}.
          {blind.length > 0 && ' (se resuelve con credenciales admin del Icecast o con el heartbeat de la app)'}
        </p>
      )}
    </div>
  );
}

const deviceShort = (c) => {
  const cl = c.client || {};
  const os = [c.platform || cl.platform, cl.device_model].filter(Boolean).join(' ');
  const br = cl.browser || '';
  return [os, br].filter(Boolean).join(' · ') || c.ua || '—';
};

function KindBadge({ kind, confidence }) {
  const meta = KIND_META[kind] || KIND_META.unknown;
  return (
    <span
      className={`inline-flex items-center gap-0.5 whitespace-nowrap rounded border px-1 py-0.5 text-[9px] font-medium ${meta.chip}`}
      title={confidence === 'inferred' ? 'Deducido del User-Agent (no confirmado por el cliente)' : 'Confirmado por el cliente'}
    >
      <span>{meta.icon}</span>
      <span>{meta.label}</span>
      {confidence === 'inferred' && <span className="opacity-60">~</span>}
    </span>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <span className="text-dark-500">{label}: </span>
      <span className={`break-all text-dark-200 ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

function ConnectionRow({ c }) {
  const [open, setOpen] = useState(false);
  const place = c.city || c.country
    ? `${flagOf(c.country_code)} ${[c.city, c.country].filter(Boolean).join(', ')}`
    : 'Sin ubicación';
  return (
    <>
      <tr className="cursor-pointer border-b border-dark-800/60 hover:bg-dark-800/40" onClick={() => setOpen(!open)}>
        <td className="px-2 py-1"><KindBadge kind={c.kind} confidence={c.confidence} /></td>
        <td className="px-2 py-1 text-[11px] text-dark-200" title={[c.city, c.region, c.country, c.isp].filter(Boolean).join(' · ')}>
          {place}
          {c.isp && <span className="text-dark-600" title={c.isp}> · {c.isp.split(' ')[0]}</span>}
        </td>
        <td className="max-w-[160px] truncate px-2 py-1 text-[11px] text-dark-400" title={deviceShort(c)}>
          {deviceShort(c)}
        </td>
        <td className="px-2 py-1 text-[11px] text-dark-300">{fmtDur(c.duration_s ?? c.connected_s)}</td>
        <td className="px-2 py-1 text-[11px] text-dark-500">{fmtAgo(c.last_ping)}</td>
      </tr>
      {open && (
        <tr className="border-b border-dark-800/60 bg-dark-900/60">
          <td colSpan={5} className="px-3 py-2">
            <div className="grid grid-cols-2 gap-x-5 gap-y-1 text-[10px] md:grid-cols-3 xl:grid-cols-4">
              <Detail label="IP" value={c.ip_masked || c.ip} mono />
              <Detail label="IP completa" value={c.ip} mono />
              <Detail label="Servidor" value={[c.server, c.mount].filter(Boolean).join(' ')} mono />
              <Detail label="Última señal" value={c.last_ping ? fmtClock(c.last_ping) : 'sin heartbeat'} />
              <Detail label="Conectado (Icecast)" value={c.connected_s != null ? fmtDur(c.connected_s) : '—'} />
              <Detail label="En el sitio desde" value={c.started_at ? fmtClock(c.started_at) : '—'} />
              <Detail label="ISP" value={c.isp} />
              <Detail label="ASN" value={c.as_name} mono />
              <Detail label="Región" value={c.region} />
              <Detail label="Zona horaria" value={c.timezone || c.tz} />
              <Detail label="Coordenadas" value={c.lat != null ? `${Number(c.lat).toFixed(3)}, ${Number(c.lon).toFixed(3)}` : '—'} mono />
              <Detail label="App / versión" value={c.kind === 'app' ? [c.app_version || 's/v', c.platform].filter(Boolean).join(' · ') : '—'} />
              <Detail label="Idioma" value={c.lang} />
              <Detail label="Referrer" value={c.referrer} mono />
              <Detail
                label="Red"
                value={[c.mobile ? 'móvil' : null, c.proxy ? 'proxy/VPN' : null, c.hosting ? 'datacenter' : null].filter(Boolean).join(', ') || 'residencial'}
              />
              <Detail label="ID de sesión" value={c.id} mono />
              <div className="col-span-2 md:col-span-3 xl:col-span-4">
                <Detail label="User-Agent" value={c.ua} mono />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function SessionsHistory() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !rows && !error) {
      try {
        const res = await api.get('/api/stats/listener-sessions', { params: { hours: 24, limit: 200 } });
        setRows(res.data.sessions || []);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      }
    }
  };

  return (
    <div className="mt-2">
      <button onClick={toggle} className="text-[10px] text-primary-400 hover:text-primary-300">
        {open ? '▾' : '▸'} Historial de sesiones (24 h)
      </button>
      {open && (
        <div className="mt-1.5 max-h-44 overflow-auto rounded-lg border border-dark-700">
          {error ? (
            <p className="p-2 text-[11px] text-error-400">Error: {error}</p>
          ) : !rows ? (
            <p className="p-2 text-[11px] text-dark-400">Cargando…</p>
          ) : rows.length === 0 ? (
            <p className="p-2 text-[11px] text-dark-400">Sin sesiones registradas todavía.</p>
          ) : (
            <table className="w-full min-w-[520px] text-left">
              <thead className="sticky top-0 bg-dark-800/80 text-[9px] uppercase text-dark-400">
                <tr>
                  <th className="px-2 py-1">Tipo</th>
                  <th className="px-2 py-1">Desde</th>
                  <th className="px-2 py-1">Dispositivo</th>
                  <th className="px-2 py-1">Visto</th>
                  <th className="px-2 py-1">Duración</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-b border-dark-800/60">
                    <td className="px-2 py-1"><KindBadge kind={s.kind} confidence={s.confidence} /></td>
                    <td className="px-2 py-1 text-[11px] text-dark-200">
                      {s.city || s.country ? `${flagOf(s.country_code)} ${s.city || s.country}` : '—'}
                    </td>
                    <td className="max-w-[150px] truncate px-2 py-1 text-[11px] text-dark-400">
                      {[s.os || s.platform, s.browser, s.device].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-2 py-1 text-[10px] text-dark-500">{new Date(s.last_seen).toLocaleTimeString('es-AR')}</td>
                    <td className="px-2 py-1 text-[11px] text-dark-300">{fmtDur(s.duration_s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function ServerChips({ servers }) {
  if (!servers || !servers.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {servers.map((s) => (
        <span
          key={s.name}
          title={
            `${s.host}${s.mount || ''} — ${s.listeners} oyente(s). ` +
            (s.detail
              ? 'Con detalle por conexión (IP/UA visibles).'
              : '⚠ El proveedor no expone IPs ni User-Agents: esas conexiones solo se cuentan; se atribuyen como App/Web únicamente si mandan heartbeat.')
          }
          className="inline-flex items-center gap-1.5 rounded border border-dark-700 bg-dark-800/60 px-2 py-0.5 text-[10px] text-dark-300"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${s.status === 'connected' ? 'bg-success-400' : 'bg-error-400'}`} />
          <span className="font-medium text-dark-200">📡 {s.name}</span>
          <b className="text-dark-100">{s.listeners}</b>
          {!s.detail && s.listeners > 0 && <span className="text-warning-400">⚠</span>}
        </span>
      ))}
    </div>
  );
}

// Modal "Registro completo" de oyentes: historial de sesiones de escucha
// (tabla listener_sessions, 30 días de retención) con descarga en Excel.
// Modal "Registro completo" de oyentes:
//  - Sesiones: TODAS las conexiones identificadas (web, app, reproductores,
//    desconocidos y monitor), con filtro por tipo y descarga Excel.
//  - Conteo: promedio y pico por hora según fuente; es el registro de las
//    conexiones "sin identificar" (servidor externo sin heartbeat).
function ListenersLogModal({ onClose }) {
  const [tab, setTab] = useState('sesiones');
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [history, setHistory] = useState(null);
  const [kindFilter, setKindFilter] = useState('all');
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    setRows(null); setHistory(null); setError(null);
    Promise.all([
      api.get('/api/stats/listener-sessions', { params: { hours: 720, limit: 2000 } }),
      api.get('/api/stats/listener-count-history', { params: { hours: 720 } }),
    ])
      .then(([s1, h1]) => {
        if (!alive) return;
        setRows(s1.data.sessions || []);
        setCounts(s1.data.counts || {});
        setHistory(h1.data.history || []);
      })
      .catch((err) => { if (alive) setError(err.response?.data?.error || err.message); });
    return () => { alive = false; };
  }, [refresh]);

  const kindLabel = (k) => (KIND_META[k] ? `${KIND_META[k].icon} ${KIND_META[k].label}` : k || '—');
  const sourceLabel = (src) =>
    src === 'icecast-external' ? 'Externa (TecnoSoul)' :
    src === 'icecast' ? 'Local (web + relay)' : src || '—';

  const visible = (rows || []).filter((r) =>
    kindFilter === 'all' ? true : r.kind === kindFilter
  );

  const fmtHour = (h) => {
    const d = new Date(h);
    return Number.isNaN(d.getTime())
      ? '—'
      : d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const downloadXlsx = () => {
    const sheets = [];
    if (rows && rows.length) {
      sheets.push({
        name: 'Sesiones',
        rows: rows.map((r) => ({
          'Última vez': fmtFull(r.last_seen),
          'Primera vez': fmtFull(r.first_seen),
          'Duración': fmtDur(r.duration_s),
          'Tipo': kindLabel(r.kind),
          'Identificación': r.confidence === 'inferred' ? 'inferida (User-Agent)' : 'confirmada',
          'Ciudad': r.city || '',
          'Región': r.region || '',
          'País': r.country || '',
          'ISP': r.isp || '',
          'ASN': r.as_name || '',
          'Dispositivo': [r.os, r.browser].filter(Boolean).join(' '),
          'Modelo': r.device || '',
          'Tipo dispositivo': r.device_type || '',
          'Plataforma': r.platform || '',
          'App versión': r.app_version || '',
          'Referrer': r.referrer || '',
          'Servidor': r.server || '',
          'Mount': r.mount || '',
          'Conectado (s)': r.connected_s != null ? r.connected_s : '',
          'IP': r.ip_masked || '',
          'IP completa': r.ip || '',
          'VPN/Proxy': r.proxy ? 'sí' : '',
          'Datacenter': r.hosting ? 'sí' : '',
        })),
        widths: { 'Última vez': 17, 'Primera vez': 17, 'Dispositivo': 18, 'ISP': 18, 'ASN': 24, 'Referrer': 22, 'Servidor': 22, 'IP completa': 16 },
      });
    }
    if (history && history.length) {
      sheets.push({
        name: 'Conteo por hora',
        rows: history.map((h) => ({
          'Fecha y hora': fmtHour(h.hour),
          'Fuente': sourceLabel(h.source),
          'Oyentes (promedio)': h.avg_listeners,
          'Pico': h.peak,
        })),
        widths: { 'Fecha y hora': 17, 'Fuente': 22 },
      });
    }
    if (sheets.length) {
      exportWorkbook(`radio-oyentes-${new Date().toISOString().slice(0, 10)}.xlsx`, sheets);
    }
  };

  const th = 'px-2 py-1.5';
  const tdc = 'px-2 py-1 text-[11px]';
  const tabBtn = (k, label) => (
    <button
      key={k}
      onClick={() => setTab(k)}
      className={`rounded px-2 py-1 text-[11px] font-medium ${
        tab === k
          ? 'border border-primary-700/50 bg-primary-900/30 text-primary-300'
          : 'border border-dark-700 bg-dark-800/60 text-dark-300 hover:bg-dark-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="flex h-[82vh] w-[min(1100px,96vw)] flex-col overflow-hidden rounded-xl border border-dark-700 bg-dark-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-dark-700 bg-dark-850 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-dark-100">📋 Registro completo · Oyentes de la radio</h3>
          <div className="flex items-center gap-1.5">
            {tabBtn('sesiones', '🗂 Sesiones')}
            {tabBtn('conteo', '📉 Conteo')}
            <button
              onClick={downloadXlsx}
              disabled={(!rows || !rows.length) && (!history || !history.length)}
              title="Descargar en Excel (.xlsx): hojas Sesiones + Conteo por hora"
              className="rounded border border-success-700/50 bg-success-900/20 px-2 py-1 text-[11px] text-success-300 hover:bg-success-900/40 disabled:opacity-40"
            >
              ⬇ Excel
            </button>
            <button onClick={() => setRefresh(refresh + 1)} title="Actualizar"
              className="rounded border border-dark-700 bg-dark-800/60 px-2 py-1 text-[11px] text-dark-300 hover:bg-dark-800">
              ↻
            </button>
            <button onClick={onClose} title="Cerrar"
              className="rounded border border-dark-700 bg-dark-800/60 px-2 py-1 text-[11px] text-dark-300 hover:bg-dark-800">
              ✕
            </button>
          </div>
        </div>

        {tab === 'sesiones' && (
          <div className="flex flex-wrap items-center gap-1 border-b border-dark-800 bg-dark-900/80 px-4 py-1.5">
            {[
              ['all', `Todos (${(rows || []).length})`],
              ['app', `App (${counts.app || 0})`],
              ['web', `Web (${counts.web || 0})`],
              ['player', `Reproductores (${counts.player || 0})`],
              ['unknown', `Desconocidos (${counts.unknown || 0})`],
              ['monitor', `Monitor (${counts.monitor || 0})`],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setKindFilter(k)}
                className={`rounded px-1.5 py-0.5 text-[10px] ${
                  kindFilter === k
                    ? 'border border-primary-700/50 bg-primary-900/30 text-primary-300'
                    : 'border border-dark-700 bg-dark-800/60 text-dark-400 hover:bg-dark-800 hover:text-dark-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {error ? (
            <p className="p-4 text-sm text-error-400">Error: {error}</p>
          ) : rows === null ? (
            <p className="p-4 text-sm text-dark-400">Cargando registro…</p>
          ) : tab === 'sesiones' ? (
            visible.length === 0 ? (
              <p className="p-4 text-sm text-dark-400">Sin sesiones de este tipo en los últimos 30 días.</p>
            ) : (
              <table className="w-full min-w-[780px] text-left">
                <thead className="sticky top-0 bg-dark-800/95 text-[10px] uppercase text-dark-400">
                  <tr>
                    <th className={th}>Última vez</th>
                    <th className={th}>Tipo</th>
                    <th className={th}>Desde</th>
                    <th className={th}>Dispositivo</th>
                    <th className={th}>App</th>
                    <th className={th}>Servidor</th>
                    <th className={th}>Duración</th>
                    <th className={th}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.id} className="border-b border-dark-800/60 hover:bg-dark-800/40">
                      <td className={`${tdc} whitespace-nowrap text-dark-500`}>{fmtFull(r.last_seen)}</td>
                      <td className={tdc}><KindBadge kind={r.kind} confidence={r.confidence} /></td>
                      <td className={`${tdc} whitespace-nowrap text-dark-200`} title={r.isp || undefined}>
                        {flagOf(r.country_code)} {[r.city, r.country].filter(Boolean).join(', ') || '—'}
                        {r.isp && <span className="text-dark-600"> · {r.isp.split(' ')[0]}</span>}
                      </td>
                      <td className={`${tdc} whitespace-nowrap text-dark-400`}>
                        {[r.os, r.browser].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className={`${tdc} text-dark-400`}>{r.kind === 'app' ? (r.app_version || 's/v') : ''}</td>
                      <td className={`${tdc} text-dark-500`}>{r.server || '—'}</td>
                      <td className={`${tdc} tabular-nums text-dark-300`}>{fmtDur(r.duration_s)}</td>
                      <td className={`${tdc} font-mono text-dark-500`}>{r.ip_masked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            history === null ? (
              <p className="p-4 text-sm text-dark-400">Cargando conteo…</p>
            ) : history.length === 0 ? (
              <p className="p-4 text-sm text-dark-400">Sin datos de conteo.</p>
            ) : (
              <table className="w-full min-w-[520px] text-left">
                <thead className="sticky top-0 bg-dark-800/95 text-[10px] uppercase text-dark-400">
                  <tr>
                    <th className={th}>Fecha y hora</th>
                    <th className={th}>Fuente</th>
                    <th className={th}>Oyentes (promedio)</th>
                    <th className={th}>Pico</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} className="border-b border-dark-800/60 hover:bg-dark-800/40">
                      <td className={`${tdc} whitespace-nowrap text-dark-400`}>{fmtHour(h.hour)}</td>
                      <td className={`${tdc} text-dark-200`}>{sourceLabel(h.source)}</td>
                      <td className={`${tdc} tabular-nums text-dark-100`}>{h.avg_listeners}</td>
                      <td className={`${tdc} tabular-nums text-dark-400`}>{h.peak}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>

        <div className="border-t border-dark-700 bg-dark-850 px-4 py-1.5 text-[10px] text-dark-500">
          {tab === 'sesiones'
            ? 'Sesiones de los últimos 30 días · todas las conexiones identificadas (web, app, reproductores y monitor)'
            : 'Conteo por hora · incluye las conexiones sin identificar del servidor externo (TecnoSoul)'}{' '}
          · actualiz\u00e1 con ↻
        </div>
      </div>
    </div>
  );
}

// Bloques grandes tipo cartel (mismo estilo que ONLINE / OYENTES / reloj):
// uno por streaming con su oyentes ahora + línea de total que coincide con
// el cartel rojo de la barra superior.
function StreamsBlocks({ geo }) {
  const ownTotal = geo.local_total ?? 0;
  const extTotal = geo.external_total ?? 0;
  const web = geo.web || 0;
  const players = geo.players || 0;
  const unknown = geo.unknown || 0;
  const app = geo.app || 0;
  const ownUnid = Math.max(ownTotal - (web + players + unknown), 0);
  const extUnid = Math.max(extTotal - app, 0);
  const glow = { textShadow: '0 0 8px rgba(255,255,255,.35)' };
  return (
    <div className="mb-2">
      <div className="grid grid-cols-2 gap-1.5">
        <div
          className="flex h-11 items-center justify-between gap-2 rounded-md border border-success-500 bg-success-600 px-3 shadow-[0_0_10px_rgba(22,163,74,.5)]"
          title="Stream propio del VPS (127.0.0.1:8000/stream): alimenta culturedj.com/radio y famadisco.com. 🌐 web · ▶️ reproductores · ❔ players sin heartbeat"
        >
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-[9px] font-bold uppercase tracking-wider text-white">📡 Propio VPS</span>
            <span className="truncate text-[8px] text-white/80">
              🌐 {web}{players > 0 ? ` · ▶️ ${players}` : ''}{ownUnid > 0 ? ` · ❔ ${ownUnid}` : ''}
            </span>
          </span>
          <b className="text-2xl font-extrabold leading-none tabular-nums text-white" style={glow}>{ownTotal}</b>
        </div>
        <div
          className="flex h-11 items-center justify-between gap-2 rounded-md border border-amber-500 bg-amber-600 px-3 shadow-[0_0_10px_rgba(245,158,11,.45)]"
          title="Relay a radios.tecnosoul.com.ar:8000/exporadio: alimenta la app Android de Expo Radio. ❔ = el proveedor no expone IP/UA (se ubicarán cuando la app apunte a nuestro stream)"
        >
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-[9px] font-bold uppercase tracking-wider text-white">📱 App Android</span>
            <span className="truncate text-[8px] text-white/80">Tecnosoul · relay{app > 0 ? ` · 📱 ${app}` : ''}{extUnid > 0 ? ` · ❔ ${extUnid}` : ''}</span>
          </span>
          <b className="text-2xl font-extrabold leading-none tabular-nums text-white" style={glow}>{extTotal}</b>
        </div>
      </div>
      <div className="mt-1 flex items-center justify-center gap-1.5 rounded-md border border-dark-700 bg-dark-900/60 px-2 py-0.5 text-[10px] text-dark-400">
        Total <b className="text-sm tabular-nums text-dark-100">{geo.total ?? ownTotal + extTotal}</b>
        <span className="text-dark-500">= propio {ownTotal} + app {extTotal} (cartel)</span>
      </div>
    </div>
  );
}

// DESDE DÓNDE ESCUCHAN AHORA: un bloque grande por ciudad/país (número con
// brillo estilo reloj), agrupado en vivo desde las conexiones identificadas,
// + un bloque ámbar para las conexiones sin identificar.
function CityTiles({ geo }) {
  const conns = (geo.connections || []).filter((c) => c.kind !== 'monitor' && c.kind !== 'bot');
  const byPlace = new Map();
  for (const c of conns) {
    const label = c.city || c.country || null;
    const key = label ? `${label}|${c.country_code || ''}` : 'unknown';
    const e = byPlace.get(key) || { key, label: label || 'Sin ubicación', cc: c.country_code, count: 0, kinds: [] };
    e.count += 1;
    if (c.kind && !e.kinds.includes(c.kind)) e.kinds.push(c.kind);
    byPlace.set(key, e);
  }
  const places = [...byPlace.values()].sort((a, b) => b.count - a.count);
  const unattr = geo.unattributed || 0;
  const kindIcon = (k) => (k === 'app' ? '📱' : k === 'web' ? '🌐' : k === 'player' ? '▶️' : '❔');
  const glow = { textShadow: '0 0 8px rgba(255,255,255,.35)' };
  return (
    <div className="mb-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[9px] font-bold uppercase tracking-wider text-dark-300">🌍 Desde dónde escuchan ahora</span>
        <span className="text-[9px] text-dark-500">{places.length > 0 || unattr > 0 ? 'en vivo' : ''}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {places.length === 0 && unattr === 0 && (
          <span className="text-[11px] text-dark-400">Nadie escuchando ahora mismo…</span>
        )}
        {places.map((p) => (
          <span
            key={p.key}
            title={`${p.label} — ${p.count} oyente(s) ${p.kinds.map(kindIcon).join(' ')}`}
            className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-dark-600 bg-black px-2.5 shadow-[0_0_8px_rgba(0,0,0,.5)]"
          >
            <span className="text-base leading-none">{p.key === 'unknown' ? '❔' : flagOf(p.cc)}</span>
            <span className="max-w-[110px] truncate text-[10px] font-bold uppercase tracking-wide text-dark-200">{p.label}</span>
            <b className="font-mono text-xl font-extrabold leading-none tabular-nums text-white" style={glow}>{p.count}</b>
          </span>
        ))}
        {unattr > 0 && (
          <span
            title="Oyentes sin heartbeat: app Android en Tecnosoul y/o players sin señal. Se contarán con ubicación cuando la app apunte a nuestro stream."
            className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-warning-700/60 bg-warning-900/30 px-2.5"
          >
            <span className="text-base leading-none">❔</span>
            <span className="text-[10px] font-bold uppercase tracking-wide text-warning-300">Sin identificar</span>
            <b className="font-mono text-xl font-extrabold leading-none tabular-nums text-white" style={glow}>{unattr}</b>
          </span>
        )}
      </div>
    </div>
  );
}

export default function ListenersPanel({ geo }) {
  const [filter, setFilter] = useState('all');
  const [streamOpen, setStreamOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const header = (showUpdated) => (
    <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2">
      <h2 className="text-sm font-semibold text-dark-100">Oyentes en vivo</h2>
      <span className="flex items-center gap-2">
        <button
          onClick={() => setLogOpen(true)}
          title="Registro completo: historial de sesiones de escucha (30 días), descargable en Excel"
          className="rounded border border-primary-700/50 bg-primary-900/20 px-1.5 py-0.5 text-[10px] font-medium text-primary-300 hover:bg-primary-900/40"
        >
          📋 Registro completo
        </button>
        {showUpdated && (
          <span className="flex items-center gap-1 text-[10px] text-dark-500" title="Heartbeat + relevamiento de Icecast cada 10 s">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-400 opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success-400" />
            </span>
            en vivo · hace {fmtAgo(geo.updated_at)}
          </span>
        )}
      </span>
    </div>
  );

  if (!geo) {
    return (
      <div className="card flex h-[450px] flex-col overflow-hidden">
        {header(false)}
        <button onClick={() => setStreamOpen(!streamOpen)} className="text-[10px] text-primary-400 hover:text-primary-300">
          {streamOpen ? '▾' : '▸'} Estado del stream
        </button>
        {streamOpen && <div className="mt-2"><StreamStatus /></div>}
        <p className="mt-2 text-[11px] text-dark-400">Esperando el primer relevamiento de oyentes (≤10 s)…</p>
      </div>
    );
  }

  const connections = geo.connections || [];
  const visible = connections.filter((c) => {
    if (filter === 'all') return c.kind !== 'monitor' && c.kind !== 'bot';
    return c.kind === filter;
  });

  const tabs = [
    { k: 'all', label: 'Todos', n: (geo.app || 0) + (geo.web || 0) + (geo.players || 0) + (geo.unknown || 0) },
    { k: 'app', label: 'App', n: geo.app || 0 },
    { k: 'web', label: 'Web', n: geo.web || 0 },
    { k: 'player', label: 'Reprod.', n: geo.players || 0 },
    { k: 'unknown', label: 'Desconoc.', n: geo.unknown || 0 },
    { k: 'monitor', label: 'Monitor', n: geo.monitor || 0 },
  ].filter((t) => t.k === 'all' || t.n > 0);

  return (
    <div className="card flex h-[450px] flex-col overflow-hidden">
      {header(true)}
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">

      {/* Los dos streamings: bloques grandes tipo cartel */}
      <StreamsBlocks geo={geo} />

      {/* DESDE DÓNDE ESCUCHAN AHORA: bloques por ciudad, tiempo real */}
      <CityTiles geo={geo} />

      {/* Conexiones en vivo: quién escucha, desde dónde y hace cuánto */}
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-dark-300">
          Conexiones {visible.length > 0 && <b className="text-dark-100">{visible.length}</b>}
        </span>
        <div className="flex flex-wrap items-center gap-1">
          {tabs.map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={`rounded px-1.5 py-0.5 text-[10px] ${
                filter === t.k
                  ? 'border border-primary-700/50 bg-primary-900/30 text-primary-300'
                  : 'border border-dark-700 bg-dark-800/60 text-dark-400 hover:bg-dark-800 hover:text-dark-200'
              }`}
            >
              {t.label} {t.n > 0 && <b>{t.n}</b>}
            </button>
          ))}
          <button
            onClick={() => setStreamOpen(!streamOpen)}
            className={`${streamOpen ? 'text-dark-200' : 'text-dark-400'} hover:text-dark-200`}
            title="Estado de la conexión a los Icecast"
          >
            {streamOpen ? '▾' : '▸'} Estado del stream
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-dark-700">
        {visible.length === 0 ? (
          <p className="p-2 text-[11px] text-dark-400">
            {connections.length === 0
              ? 'Sin conexiones identificadas. Los oyentes aparecen ~10 s después de conectarse.'
              : 'Ninguna conexión de este tipo ahora mismo.'}
          </p>
        ) : (
          <div>
            <table className="w-full min-w-[520px] text-left">
              <thead className="sticky top-0 bg-dark-800/80 text-[9px] uppercase text-dark-400">
                <tr>
                  <th className="px-2 py-1">Tipo</th>
                  <th className="px-2 py-1">Desde</th>
                  <th className="px-2 py-1">Dispositivo</th>
                  <th className="px-2 py-1">Conectado</th>
                  <th className="px-2 py-1">Visto</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => <ConnectionRow key={c.id} c={c} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {streamOpen && <div className="mt-1.5"><StreamStatus /></div>}

      <SessionsHistory />

      </div>

      {logOpen && <ListenersLogModal onClose={() => setLogOpen(false)} />}
    </div>
  );
}
